import {vec3} from "gl-matrix";
import {Component, Object3D, PhysXComponent} from "@wonderlandengine/api";
import {property} from "@wonderlandengine/api/decorators.js";
import {TouchUI} from "./touch-ui.js";
import { PlayerMovementHandler } from "./player-movement-handler.js";
import { vectorProjection } from "./utils/utils.js";

/**
 * Basic movement with touch events on the left half of the screen.
 */
export class TouchMove extends Component {
	static TypeName = "touch-move";

    @property.float(2000)
    acceleration: number = 2000.0;

    @property.float(2.5)
    maxSpeed: number = 2.5;

	@property.object({required: true})
	nonVRCamera!: Object3D;

	@property.object({required: true})
	touchUIComponent!: Object3D;    

    @property.float(0.1)
    private lowThreshold: number = 0.1;

	@property.float(90)
	private rotationSpeed: number = 90.0; // degrees per second
	
	@property.object()
    playerObject: Object3D | null = null; //must have a player-movement-handler component

	private headForward: vec3 = [0, 0, 0];
	private touchUI!: TouchUI;
	private physxComp: PhysXComponent | null = null;
    private currentRotationY = 0;
	private _playerMovementHandler: PlayerMovementHandler | null = null;
    private origin = vec3.create();
	private parentOrigin = vec3.create();

	start() {
		const tempTouchUI = this.touchUIComponent.getComponent(TouchUI);
		if (!tempTouchUI) {
			throw new Error(
				`touch-look(${this.object.name}): touchUIComponent does not have a TouchUI`,
			);
		}
		this.touchUI = tempTouchUI;
		this.touchUI.onJumpPressed.add(this.jumpPressed.bind(this));

        const tempPlayerMove = this.playerObject?.getComponent(PlayerMovementHandler);
        if (!tempPlayerMove) {
            throw new Error(
                "The touch-move component requires a player-movement-handler component on playerObject",
            );
        }

		const tempPhysxComp = this.object.getComponent(PhysXComponent);
		if (!tempPhysxComp) {
			throw new Error(
				"the touch-move component requires a PhysX component on the same object",
			);
		}
		
        this.physxComp = tempPhysxComp
		this._playerMovementHandler = tempPlayerMove;
	}

	update(delta: number) {
		if (this.touchUI.active && this.touchUI.joystick1Value.isTouching) {
			const movementX =
				this.touchUI.joystick1Value.x * this.acceleration * delta;
			const movementY =
				this.touchUI.joystick1Value.y * this.acceleration * delta;

			// Get the forward direction of the headObject
			this.headForward = [0, 0, 0];
			const direction: vec3 = [1, 0, 1];

			// Move according to headObject Forward Direction
			vec3.normalize(direction, direction);
			direction[0] *= movementX;
			direction[2] *= movementY;

			this.nonVRCamera.getForwardWorld(this.headForward);

			// Combine direction with headObject
			vec3.transformQuat(
				direction,
				direction,
				this.nonVRCamera.getTransformWorld(),
			);

			// Do not move on Y axis
			direction[1] = 0;

			let currentSpeed = 0;
			if (this.physxComp) {
				const horizontalVelocity = vec3.fromValues(
					this.physxComp.linearVelocity[0],
					0,
					this.physxComp.linearVelocity[2]
				);
				currentSpeed = vec3.length(horizontalVelocity);
				if (Math.abs(currentSpeed) > this.maxSpeed) {
					// Subtract only the horizontal component to preserve vertical movement
					vec3.subtract(direction, direction, vectorProjection(direction, horizontalVelocity));
				}
				// Add force to Physx Component to move the player
				this._playerMovementHandler?.requestMovement(direction);
			}
		}

        if (this.touchUI.active && this.touchUI.joystick2Value.isTouching) {
			const currentAxis = this.touchUI.joystick2Value.x;
			if (Math.abs(currentAxis) >= this.lowThreshold) {
				const yawRate = -currentAxis * this.rotationSpeed;
				if (this._playerMovementHandler) {
					this._playerMovementHandler.requestYawDeltaDeg(yawRate);
				}
			}

			const nodRotationY = -this.touchUI.joystick2Value.y;
			if (Math.abs(nodRotationY) >= this.lowThreshold) {

				this.currentRotationY += nodRotationY * delta;

				/* 1.507 = PI/2 = 90° */
				this.currentRotationY = Math.min(1.507, this.currentRotationY);
				this.currentRotationY = Math.max(-1.507, this.currentRotationY);

				this.nonVRCamera.getPositionWorld(this.origin);

				const parent = this.nonVRCamera?.parent;
				if (parent) {
					parent.getPositionWorld(this.parentOrigin);
					vec3.subtract(this.origin, this.origin, this.parentOrigin);
				}

				this.nonVRCamera.resetPositionRotation();
				this.nonVRCamera.rotateAxisAngleRadLocal([1, 0, 0], this.currentRotationY);
				this.nonVRCamera.translateLocal(this.origin);
			}
		}
		
	}

	jumpPressed() {
        this._playerMovementHandler?.requestJump();
	}
}
